/**
 * "Move in a resident" — the EHR-managed variant.
 *
 * Replaces moveInResident on campuses where the EHR owns resident data
 * (`ctx.ehrActive`, i.e. an active EHR vendor that is receiving residents —
 * see session.js `isResidentDataEhrManaged`). There the resident is created in the
 * partner system and arrives here as a task to approve on /tasks/ehr, so the
 * portal's job is approval, not data entry.
 *
 * Same title as the portal flow on purpose: the user's task is "move in a
 * resident", and which procedure that means is the campus's business, not
 * something they should have to choose between in the picker. The gates are
 * exactly complementary so only ever one of the two is offered.
 *
 * `placement` is omitted throughout — the field is vestigial since the callout
 * bubble was replaced by the spotlight ring (SpotlightOverlay.jsx).
 *
 * See docs/INTERACTIVE_HELP_PLAN.md §5.2 for the anchors and §10 Decision 2.
 */

export default {
    id: 'move-in-resident-ehr',
    title: 'Move in a resident',
    description: 'Approve the move-in task sent by your EHR.',

    minRoleWeight: 11,

    /* Exact inverse of moveInResident. */
    available: (ctx) => ctx.ehrActive,

    startsAt: 'This will take you to the EHR Tasks page.',

    steps: [
        {
            id: 'open-subtab',
            route: { path: '/tasks/ehr', navigate: true },
            anchor: 'ehr.subtab-add',
            title: 'Open the Move-in Resident tasks',
            body: 'On the Residents tab, choose Move-in Resident. The number on the tab is how many tasks are waiting. If you are looking at the Users tab, switch to Residents first.',
            /* Move-in is the tab the page opens on, so this is normally already
               satisfied and the step ticks itself the moment the page mounts —
               which is the right outcome, not a special case to code around. It
               still earns its place for the user who arrives with another subtab
               already selected.

               Keyed on `.is-active` rather than [aria-selected="true"]: the
               engine's MutationObserver filters attributes to class/style/disabled
               (detectors.js), so the class flip is caught instantly while
               aria-selected would wait for the 250ms poll. */
            done: { type: 'dom', selector: '[data-help-id="ehr.subtab-add"].is-active' },
        },
        {
            id: 'open-task',
            route: { path: '/tasks/ehr', navigate: false },
            anchor: 'ehr.task-row',
            title: 'Open a move-in task',
            body: 'Click the row for the resident you are moving in. If the list is empty there is nothing waiting for approval right now. Rows marked with a lock are scheduled for a future date and cannot be approved yet.',
            /* The approve button only exists while the dialog is mounted, so its
               presence is exactly "the task is open". */
            done: { type: 'dom', selector: '[data-help-id="ehr.approve-add"]' },
        },
        {
            id: 'approve',
            route: { path: '/tasks/ehr', navigate: false },
            anchor: 'ehr.approve-add',
            title: 'Check the details, then approve',
            body: 'Read the room and name the EHR sent, optionally tick any groups this resident belongs to, then click Approve. Their pendant devices still need attaching afterwards — the guide for that is "Assign a pendant to a resident".',
            /* Presence, not :not([disabled]) — the dialog re-disables Approve
               while the write is in flight, and an enabled-state test would read
               that as the user backing out (same trap as moveOutResident step 4). */
            context: {
                selector: '[data-help-id="ehr.approve-add"]',
                contextStep: 'open-task',
            },
            done: { type: 'transaction', transTypes: ['resident_add_task'] },
            timeoutMs: 15000,
        },
    ],

    success: {
        title: 'Move-in approved',
        body: 'The resident is now active in the portal. Attach their pendant devices next so their alerts are monitored.',
    },
};
